import { formatTimeStr, generateUUID, timeFormat } from '@/utils/other';
import { BATCH_CREATED_TODO_EVENT, CREATED_TODO_EVENT, pubsub, UPDATE_TODO_EVENT } from '@/utils/pubsub';
import { generateOptions, addOptions } from '@/utils/todo';
import moment from 'moment';
import BaseListClass from './BaseListClass';
import ListClass from './ListClass';
import RequestApi from './RequestApiClass';

type IGetTodoParams = ITodo.IModel.IGetTodoParams;
type ISearchTodo = ITodo.IRequest.ISearchTodo;
type ITodoInfo = ITodo.IResponse.ITodoInfo;
type ICreateTodoParams = ITodo.IRequest.ICreateTodoParams;
type IOptionInfo = ITodo.IOptionInfo;
type IRecordInfo = ITodo.IResponse.IRecordInfo;


interface IReadyInfoForCreateTodo {
  moduleId: string;
  moduleTitle: string;
  moduleDue: string;
  projectName: string;
  projectId: string;
}

const defaultParams: IGetTodoParams = {
  returnComment: false,
  returnRecord: true,
};

const sortRecords = (records: IRecordInfo[]): IRecordInfo[] => {
  const cloneRecords = records.concat([]); // 复制新的数组，避免改动原数组
  return cloneRecords.sort((a: IRecordInfo, b: IRecordInfo): number => {
    return moment(a.checkIn).valueOf() - moment(b.checkIn).valueOf();
  });
};

export default class TodoListClass extends BaseListClass<ITodoInfo> {
  public $requestParams: ISearchTodo = {...defaultParams};
  public remindRefresh = false;
  private $todoForCreate: ICreateTodoParams | undefined = undefined;
  private $typeOption: IOptionInfo[] = [];
  private $ownerOption: IOptionInfo[] = [];
  private $statusOption: IOptionInfo[] = [];
  private $projectOption: IOptionInfo[] = [];
  private $settlementUnitOption: IOptionInfo[] = [];
  get todoForCreate() {
    return this.$todoForCreate;
  }
  get typeOption() {
    return this.$typeOption;
  }
  get ownerOption() {
    return this.$ownerOption;
  }
  get statusOption() {
    return this.$statusOption;
  }
  get projectOption() {
    return this.$projectOption;
  }
  get settlementUnitOption() {
    return this.$settlementUnitOption;
  }
  constructor() {
    super({
      list: new ListClass<ITodoInfo>(),
      requestApi: new RequestApi('Todo'),
    });
  }
  public requestMoreData(): Promise<string> {
    this.$requestParams.pageindex! += 1;
    return new Promise((reslove, reject) => {
      this.$requestApi
        .getData(this.$requestApi.baseUrl, this.$requestParams)
        .then((res) => {
          this.addTodos(res.data);
          this.updateIsMore(res.data.length);
          reslove(res.message);
        })
        .catch((error) => {
          reject(error.message);
        });
    });
  }
  public requestBatchCreateTodo(params: ITodo.IRequest.IBatchCreateTodoParams[]): Promise<string> {
    return new Promise((reslove, reject) => {
      this.$requestApi.postData(
        this.requestApi.createUrl(['batch', 'add']),
        params,
      ).then((res) => {
        pubsub.publish(BATCH_CREATED_TODO_EVENT, params[0].moduleId);
        reslove(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestCheckInTodo(todo: ITodoInfo): Promise<string> {
    return new Promise((reslove, reject) => {
      this.$requestApi.postData(
        this.$requestApi.createUrl([todo.id, 'in']),
        null,
      ).then((res) => {
        this.todoAddRecord(todo);
        reslove(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestCheckOutTodo(todo: ITodoInfo): Promise<string> {
    return new Promise((reslove, reject) => {
      this.$requestApi.postData(
        this.$requestApi.createUrl([todo.id, 'out']),
        null,
      ).then((res) => {
        this.todoUpdateRecord(todo);
        reslove(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestUpdateTodoStatus(todo: ITodoInfo, status: ITodo.ITodoStatus, comment?: string): Promise<string> {
    return new Promise((reslove, reject) => {
      this.$requestApi
        .putData(this.$requestApi.createStatusUrlById(todo.id), null, {
          status,
          comment: comment ? comment : null,
        })
        .then((res) => {
          const newTodo = { ...todo, status };
          this.$list.updateItem(newTodo);
          pubsub.publish(UPDATE_TODO_EVENT, newTodo);
          reslove(res.message);
        })
        .catch((error) => {
          reject(error.message);
        });
    });
  }
  public requestUndoTodo(todo: ITodoInfo, comment?: string): Promise<string> {
    return new Promise((reslove, reject) => {
      this.$requestApi
        .postData(this.$requestApi.createUrl([todo.id, 'undo']), null, {
          comment: comment ? comment : null,
        })
        .then((res) => {
          this.$list.deleteItemById(todo.id);
          reslove(res.message);
        })
        .catch((error) => {
          reject(error.message);
        });
    });
  }
  public requestTransAcceptor(todo: ITodoInfo, acceptanceBy: string): Promise<string> {
    return new Promise((reslove, reject) => {
      this.$requestApi
        .putData(this.$requestApi.createStatusUrlById(todo.id), null, {
          status: todo.status,
          acceptanceBy,
        })
        .then((res) => {
          this.$list.updateItem({...todo, acceptanceBy});
          reslove(res.message);
        })
        .catch((error) => {
          reject(error.message);
        });
    });
  }
  public convertData(todos: ITodoInfo[]): ITodoInfo[] {
    return todos.map((todo) => {
      const records = sortRecords(todo.records); // 必须按checkIn时间排序，支持后面的逻辑
      return {
        ...todo,
        due: todo.due ? formatTimeStr(todo.due) : '',
        createdOn: timeFormat(todo.createdOn),
        records,
        isChecedOut: this.isChecedOutByTodo(records),
      };
    });
  }
  public initListData(todos: ITodoInfo[]) {
    this.$list.initData(this.convertData(todos));
    this.generateOptions(this.list.data, true);
    if (!this.$list.current) {
      return;
    }
    if (this.$list.checkItemIsInData(this.$list.current.id)) {
      this.$list.changeCurrent(this.list.findItemById(this.$list.current.id));
    } else {
      this.$list.changeCurrent(undefined);
    }
  }
  public addTodos(todos: ITodoInfo[]) {
    const todoInfos = this.convertData(todos);
    this.$list.pushItemList(todoInfos);
    this.generateOptions(todoInfos, false);
  }
  public createTodo(module: IReadyInfoForCreateTodo) {
    this.$todoForCreate = {
      typeId: '',
      typeName: '',
      isBonus: false,
      manDay: 0,
      title: '',
      description: '',
      owner: '',
      due: '',
      settlementConfigurationId: '',
      moduleId: module.moduleId,
      moduleTitle: module.moduleTitle,
      moduleDue: module.moduleDue,
      projectName: module.projectName,
      projectId: module.projectId,
      comment: '',
    };
    return this.$todoForCreate;
  }
  public requestDataForUnshift(todoId: string): Promise<string> {
    return new Promise((reslove, reject) => {
      this.$requestApi
        .getData(this.$requestApi.baseUrl, { id: todoId })
        .then((res) => {
          if (res.data.length > 0) {
            const todoInfo = this.convertData(res.data)[0];
            this.unshiftToList(todoInfo);
            this.generateOptions([todoInfo], false);
            pubsub.publish(CREATED_TODO_EVENT, todoInfo);
            reslove(res.message);
          } else {
            reject('Error');
          }
        })
        .catch((err) => {
          reject(err.messgae);
        });
    });
  }
  public updateTodoBonus(todoIds: string[]) {
    for (const todoInfo of this.$list.data) {
      const index = todoIds.findIndex((id) => id === todoInfo.id);
      if (index > -1) {
        todoInfo.isBonus = true;
        todoIds.splice(index, 1);
      }
      if (todoIds.length === 0) {
        break;
      }
    }
  }
  public initRequestParams() {
    this.$requestParams = {...defaultParams};
  }
  public generateOptions = (todoList: ITodoInfo[], isInit: boolean): void => {
    const {typeOption, ownerOption, statusOption, projectOption, settlementUnitOption} = generateOptions(todoList);
    if (isInit) {
      this.$typeOption = [...typeOption];
      this.$ownerOption = [...ownerOption];
      this.$statusOption = [...statusOption];
      this.$projectOption = [...projectOption];
      this.$settlementUnitOption = [...settlementUnitOption];
      return;
    }
    this.$typeOption = addOptions(this.$typeOption, typeOption);
    this.$ownerOption = addOptions(this.$ownerOption, ownerOption);
    this.$statusOption = addOptions(this.$statusOption, statusOption);
    this.$projectOption = addOptions(this.$projectOption, projectOption);
    this.$settlementUnitOption = addOptions(this.$settlementUnitOption, settlementUnitOption);
  }
  public todoAddRecord(todo: ITodoInfo) {
    const newRecord: IRecordInfo = {
      checkIn: moment().toJSON(),
      checkOut: '',
      comment: null,
      id: generateUUID(),
      todoId: todo.id,
    };
    todo.records.push(newRecord);
    todo.isChecedOut = this.isChecedOutByTodo(todo.records);
  }
  public todoUpdateRecord(todo: ITodoInfo) {
    const len = todo.records.length;
    if (len === 0) {
      return;
    }
    todo.records[len - 1].checkOut = moment().toJSON();
    todo.isChecedOut = this.isChecedOutByTodo(todo.records);
  }
  public updateStatus(todo: ITodoInfo, status: ITodo.ITodoStatus): void {
    todo.status = status;
    this.$list.updateItem({...todo});
    pubsub.publish(UPDATE_TODO_EVENT, todo);
  }
  public isChecedOutByTodo(todoRecords: IRecordInfo[]) {
    // 根据 records 最后一个记录是否有签出时间判断
    if (todoRecords.length > 0) {
      return todoRecords.slice(-1)[0].checkOut ? true : false;
    } else {
      return true;
    }
  }
}
export const todoListInstance = new TodoListClass();
